import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Loader2, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import { CreateInviteDialog } from "@/components/CreateInviteDialog";
import { CandidateFormFields, emptyCandidate, formToCandidatePayload } from "@/components/CandidateFormFields";
import { VisibilityBadge } from "@/components/VisibilityBadge";

const AVAIL_COLOR = { open: "#10B981", passive: "#F5A623", not_looking: "#71717A", placed: "#002EA6" };

const empty = emptyCandidate;

export default function Candidates() {
  const meta = useMeta();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState(null);
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (avail !== "all") params.availability = avail;
    api.get("/candidates", { params }).then(({ data }) => setCandidates(data));
  }, [q, avail]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/candidates", formToCandidatePayload(form));
      toast.success("Candidate added");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader overline="Talent database" title="Candidates">
        <CreateInviteDialog />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-candidate-btn" className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white transition-colors">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Add Candidate</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <CandidateFormFields form={form} setForm={setForm} meta={meta} />
              <DialogFooter>
                <Button type="submit" data-testid="cand-save-btn" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Candidate"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Filters */}
      <div className="px-8 py-4 border-b border-black/10 bg-white flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="cand-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, headline, email…" className="bg-[#F4F4F5] border-black/10 rounded-none pl-9" />
        </div>
        <Select value={avail} onValueChange={setAvail}>
          <SelectTrigger className="w-44 bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue placeholder="Availability" /></SelectTrigger>
          <SelectContent className="bg-[#F4F4F5] border-black/10">
            <SelectItem value="all">All availability</SelectItem>
            {meta.availability.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="p-8">
        {!candidates && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {candidates && candidates.length === 0 && <Empty icon={Users} title="No candidates found" hint="Add candidates to build your talent database." />}
        {candidates && candidates.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {candidates.map((c) => (
              <button key={c.id} data-testid={`cand-card-${c.id}`} onClick={() => navigate(`/app/candidates/${c.id}`)}
                className="group bg-white p-5 text-left hover:bg-[#F4F4F5] transition-colors duration-150">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 bg-black/10 flex items-center justify-center font-heading font-bold overflow-hidden" data-testid={`cand-avatar-${c.id}`}>
                    {c.photo_url
                      ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt={c.full_name} className="h-full w-full object-cover" />
                      : c.full_name.charAt(0)}
                  </div>
                  <span className="flex items-center gap-1.5 text-[0.6rem] font-mono uppercase tracking-wider">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: AVAIL_COLOR[c.availability_status] || "#71717A" }} />
                    {(c.availability_status || "").replace(/_/g, " ")}
                  </span>
                </div>
                <h3 className="font-heading font-bold mt-4 group-hover:text-primary transition-colors">{c.full_name}</h3>
                <p className="text-sm text-muted-foreground truncate">{c.headline || "—"}</p>
                <div className="mt-2"><VisibilityBadge status={c.visibility_status} testid={`vis-badge-${c.id}`} /></div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(c.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[0.6rem] font-mono px-2 py-0.5 border border-black/10 text-muted-foreground">{t}</span>
                  ))}
                  {(c.board_interest) && <span className="text-[0.6rem] font-mono px-2 py-0.5 border border-primary/40 text-primary">BOARD</span>}
                  {(c.fractional_interest) && <span className="text-[0.6rem] font-mono px-2 py-0.5 border border-primary/40 text-primary">FRACTIONAL</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
